import { useEffect, useRef, useState } from 'react'
import { Trash2, Camera } from 'lucide-react'
import PropTypes from 'prop-types'

import { ConfirmModal } from '@components/common'
import { GENERIC_MESSAGES, BUTTONS_DISPLAY_LABEL } from '../../../constants'
import { AdasBox, AdasButton, AdasTooltip, AdasTypography, AdasImage } from '@components/wrapper-components'
import { AdasLoader } from '@components/adas-loader'
import { Logger } from '../../../logger'
import { PhotoViewerWithoutThumbnail } from '../photo-viewer-without-thumbnail'

const DeleteMe = ({ whenDelete }) => {
  return (
    <AdasTooltip title='Delete'>
      <AdasButton buttonType='iconButton' size='medium' className='absolute top-2 right-2' onClick={whenDelete}>
        <Trash2 className='w-4 h-4' style={{ color: '#ef5350' }} />
      </AdasButton>
    </AdasTooltip>
  )
}

export const PhotoInputWithoutThumbnail = ({
  parentId,
  labelText,
  title,
  imageLocation,
  onUpdate,
  allowDelete = false,
  imageDataObj,
  fetchImage,
  disabled = false
}) => {
  const fileRef = useRef(null)
  const [isModalOpen, setModalOpen] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [imageUrl, setImageUrl] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchImageData = async () => {
      if (imageDataObj?.id) {
        try {
          setLoading(true)
          const imageData = await fetchImage(imageDataObj.id)
          const url = URL.createObjectURL(imageData.data)
          setImageUrl(url)
        } catch (error) {
          Logger.error({ message: error, payload: { path: 'PhotoInput/getSupportPhoto', imageDataObj } })
        } finally {
          setLoading(false)
        }
      } else {
        setImageUrl(imageDataObj?.imageUrl)
      }
    }

    fetchImageData()
  }, [imageDataObj])

  const openModal = () => {
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
  }

  const handleUpload = async (ref) => {
    const file = ref.current.files[0]
    if (file) {
      const formData = new FormData()
      formData.append('file', file, file.name)
      setImageUrl(URL.createObjectURL(file))
      onUpdate(formData, imageLocation, URL.createObjectURL(file))
    }
  }

  const deleteAttachment = async () => {
    setShowConfirmModal(false)
    setImageUrl('')
    onUpdate(null, imageLocation, null)
  }

  const deleteWithConfirmation = () => {
    setShowConfirmModal(true)
  }

  return !loading && !imageDataObj ? (
    <AdasBox className='flex items-center justify-center rounded-sm mb-[20px] h-[200px] border border-dashed border-divider'>
      <AdasButton component='label' className='flex items-center m-auto text-xs' startIcon={<Camera />} disabled={disabled}>
        <AdasTypography className='text-xs'>{labelText}</AdasTypography>
        <input ref={fileRef} type='file' hidden accept='image/*' onChange={() => handleUpload(fileRef)} />
      </AdasButton>
    </AdasBox>
  ) : (
    <AdasBox className='flex flex-col items-center mb-[20px]'>
      <AdasBox id='image' className='relative min-w-[150px]'>
        {/* Show the loader from the moment we know a remote image is on the way
            (imageDataObj has an id) until imageUrl is hydrated — covers both
            the pre-effect window and the actual in-flight fetch. */}
        {loading || (imageDataObj?.id && !imageUrl) ? (
          <AdasLoader className='min-w-full sm:min-w-[300px] rounded-sm h-[200px]' />
        ) : (
          <AdasImage src={imageUrl} alt={title} className='w-full object-contain rounded-sm h-[200px]' />
        )}
        {allowDelete && !disabled && imageDataObj && <DeleteMe whenDelete={deleteWithConfirmation} />}
        <>
          <AdasButton
            component='label'
            className='flex items-center text-sm absolute rounded-none pl-[20px] rounded-bl-[4px] rounded-br-[4px] text-white left-0 right-0 bottom-0 bg-[#1A1B1F80]'
            startIcon={<Camera />}
          >
            <AdasButton onClick={openModal} variant='button-small'>
              <AdasTypography variant='button-small' className='flex-1 whitespace-nowrap'>
                View Photo
              </AdasTypography>
            </AdasButton>
          </AdasButton>
          <PhotoViewerWithoutThumbnail
            parentId={parentId}
            fetchImage={fetchImage}
            isOpen={isModalOpen}
            closeModal={closeModal}
            imageUrl={imageUrl}
            imageDataObj={imageDataObj}
            imageLocation={imageLocation}
            onUpdate={onUpdate}
            allowDelete={allowDelete}
            disabled={disabled}
          />
        </>
      </AdasBox>
      {showConfirmModal && (
        <ConfirmModal
          cancelLabel={BUTTONS_DISPLAY_LABEL.CANCEL}
          confirmLabel={BUTTONS_DISPLAY_LABEL.DELETE}
          open={showConfirmModal}
          title={GENERIC_MESSAGES.DELETE_PHOTO}
          message={GENERIC_MESSAGES.CONFIRM_DELETE_PHOTO}
          handleClose={() => setShowConfirmModal(false)}
          handleContinue={deleteAttachment}
        />
      )}
    </AdasBox>
  )
}

PhotoInputWithoutThumbnail.propTypes = {
  labelText: PropTypes.string,
  imageLocation: PropTypes.string,
  onUpdate: PropTypes.func,
  allowDelete: PropTypes.bool,
  disabled: PropTypes.bool,
  img_rotation: PropTypes.number,
  title: PropTypes.string,
  fetchImage: PropTypes.func,
  imageDataObj: PropTypes.object
}
